import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import ReactDOM from 'react-dom'

import { ThemeProvider, useMediaQuery, useTheme } from '@mui/material'

import { useTranslation } from 'language/translator'

/* Types */
import { TCustomization } from 'types/GlobalCustomization'
import {
  DashboardVariantType,
  ILayoutItem,
  TDashboard
} from 'types/GlobalDashboard'
import {
  DashboardFilter,
  ParsedDashboardFilterOptions
} from 'types/GlobalDashboardFilter'
import {
  CustomPeriodFilter,
  InnerWidgetPatchBody,
  isDashboardWidgetFullyLoaded,
  isDashboardWidgetSettingsLoaded,
  ParsedSegmentPath,
  PeriodFilter,
  TCoreWidget,
  TDashboardWidget,
  TViewport,
  TWidgetSettings,
  WidgetOrder,
  WidgetType
} from 'types/GlobalWidget'

import Backdrop from 'components_new/atoms/Backdrop'
import Button from 'components_new/atoms/Button'
import Box from 'components_new/atoms/Box'
import Collapse from 'components_new/atoms/Collapse'

import BreakdownBanner from 'components_new/organisms/BreakdownBanner'
import DashboardUserFilterBar from 'components_new/organisms/DashboardUserFilterBar'

import { getTheme, getWidgetMode } from 'themes'
import { formatWidgetData } from 'utils/widgetData'

import WidgetBase from './WidgetBase'
import WidgetBody from './WidgetBody'
import WidgetFooter from './WidgetFooter'
import WidgetHeader from './WidgetHeader'
import WidgetMenu from './WidgetMenu'
import WidgetSettings from './WidgetSettings'
import { useLanguage } from 'language/translator'
import { SETTINGS_DRAWER_SX } from 'components_new/molecules/SettingsDrawer'

interface WidgetProps {
  availableSpace: ILayoutItem | null
  customization?: TCustomization
  customPeriodFilter?: CustomPeriodFilter | null
  customSegmentBy?: string | null
  disableGutter?: boolean
  dashboard?: TDashboard
  dashboardFilter: DashboardFilter
  dashboardFilterOptions: Map<string, ParsedDashboardFilterOptions>
  editMode: boolean
  embedded?: boolean
  fetchWidgetData: (widget: TCoreWidget) => void
  interactive?: boolean
  isAdmin: boolean
  periodFilter?: PeriodFilter | null
  plain?: boolean
  resetAllDashboardTempStates: () => void
  scaleFactor: number
  setCustomSegmentBy?: (
    attributeOptionId: string | null,
    dbFilter?: DashboardFilter,
    periodFilter?: CustomPeriodFilter
  ) => void
  setDashboardFilter?: (
    dbFilter: DashboardFilter,
    periodFilter?: CustomPeriodFilter
  ) => void
  setPeriodFilter?:
    | ((value: PeriodFilter | CustomPeriodFilter | null) => void)
    | ((value: CustomPeriodFilter | null) => void)
  showUnderlyingContent?: (
    settings: TWidgetSettings,
    segmentPaths: ParsedSegmentPath[],
    initialKpiOptionId: string | null
  ) => void
  standaloneWidget?: boolean
  updateWidget?: (arg0: string, arg1: InnerWidgetPatchBody) => void
  variantType: DashboardVariantType
  widget: TDashboardWidget
}

export const STANDARD_WIDGET_SCALE_FACTOR = 0.8

// No manual header-click sort applied - the table falls back to the
// widget's own configured default sort (settings.sort_by)
const INITIAL_ORDER: WidgetOrder = { id: null, index: null, dir: 'asc' }

const Widget = (props: WidgetProps) => {
  const {
    availableSpace,
    customization,
    customPeriodFilter = null,
    customSegmentBy = null,
    dashboard,
    dashboardFilter,
    dashboardFilterOptions,
    disableGutter,
    editMode = false,
    embedded = false,
    fetchWidgetData,
    interactive = true,
    isAdmin = false,
    periodFilter = null,
    plain = false,
    scaleFactor,
    setCustomSegmentBy = () => {},
    setDashboardFilter = () => {},
    showUnderlyingContent = () => {},
    standaloneWidget = false,
    updateWidget = () => {},
    variantType,
    widget
  } = props
  const { lang } = useLanguage()
  const [fullSizeDialogOpen, setFullSizeDialogOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const wasFullSizeBeforeSettingsRef = useRef(false)
  const [order, setOrder] = useState<WidgetOrder>(INITIAL_ORDER)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('mobile'), {
    noSsr: true
  })

  const widgetMode = getWidgetMode(customization)
  const widgetTheme = getTheme(widgetMode, customization)
  const { t } = useTranslation()

  // For MAP only, required in both settings and widget:
  // TODO: Move viewport state to parent component or context
  const [viewport, setViewport] = useState<TViewport>({
    longitude: widget.settings?.viewport.longitude ?? 0,
    latitude: widget.settings?.viewport.latitude ?? 0,
    pitch: widget.settings?.viewport.pitch ?? 0,
    zoom: widget.settings?.viewport.zoom ?? 0
  })

  /*-- For Map beta: --*/
  const [fakeLoading, setFakeLoading] = useState(false)

  function triggerFakeLoading() {
    setFakeLoading(true)

    setTimeout(() => setFakeLoading(false), 200)
  }

  // Auto-expand widget when settings are open
  useEffect(() => {
    if (settingsOpen) {
      wasFullSizeBeforeSettingsRef.current = fullSizeDialogOpen

      if (!fullSizeDialogOpen) {
        handleOpenFullSize()
      }
    } else {
      if (!wasFullSizeBeforeSettingsRef.current && fullSizeDialogOpen) {
        handleCloseFullSize()
      }
    }
  }, [settingsOpen])

  // Auto-fetch widget data when it becomes undefined or null
  // Listen to dataVersion instead of data to handle rapid invalidations
  useEffect(() => {
    if (!isDashboardWidgetFullyLoaded(widget)) {
      fetchWidgetData(widget)
    }
  }, [widget.dataVersion])

  // Reset any manual header-click sort once the widget's own default
  // sort changes (eg. saved in settings), so a stale click doesn't
  // keep overriding the newly configured default
  useEffect(() => {
    setOrder(INITIAL_ORDER)
  }, [
    widget.settings?.sort_by.selected,
    widget.settings?.sort_by.visualization_constraint
  ])

  const formattedData = useMemo(() => {
    if (isDashboardWidgetFullyLoaded(widget)) {
      return formatWidgetData(widget.data, widget.settings, lang)
    }

    return null
  }, [widget])

  const handleShowUnderlyingContent = (
    segmentPaths: ParsedSegmentPath[],
    initialKpiOptionId: string | null
  ) => {
    if (isDashboardWidgetSettingsLoaded(widget)) {
      showUnderlyingContent(widget.settings, segmentPaths, initialKpiOptionId)
    }
  }
  const handleUpdateWidgetSettings = (body: InnerWidgetPatchBody) => {
    // Remove when map refresh bug is solved:
    // Trigger fake loading for MAP widgets to force refresh
    if (widget.type === WidgetType.MAP) {
      triggerFakeLoading()
    }

    updateWidget(widget.id, body)
  }

  // Animation state: 'idle' | 'expanding' | 'expanded' | 'collapsing'
  const [animState, setAnimState] = useState<
    'idle' | 'expanding' | 'expanded' | 'collapsing'
  >('idle')
  const originRectRef = useRef<DOMRect | null>(null)

  // True while the widget needs fullscreen CSS
  // (including during collapse animation)
  const useFullSizeLayout = fullSizeDialogOpen || animState === 'collapsing'

  const activeScaleFactor = fullSizeDialogOpen
    ? STANDARD_WIDGET_SCALE_FACTOR
    : scaleFactor

  const contentRef = useRef<HTMLDivElement>(null)
  const inlineSlotRef = useRef<HTMLDivElement>(null)

  const handleOpenFullSize = useCallback(() => {
    // Capture the widget's current position before moving
    if (contentRef.current) {
      originRectRef.current = contentRef.current.getBoundingClientRect()
    }

    setFullSizeDialogOpen(true)
    setAnimState('expanding')
  }, [])

  const handleCloseFullSize = useCallback(() => {
    setAnimState('collapsing')
    setFullSizeDialogOpen(false)
  }, [])

  // Close fullscreen on Escape key
  useEffect(() => {
    if (!fullSizeDialogOpen) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCloseFullSize()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [fullSizeDialogOpen, handleCloseFullSize])

  // Move the content DOM node to #root (not document.body) to escape the
  // grid item's CSS transform that breaks position:fixed, while keeping
  // React event delegation working (events bubble through #root).
  const reactRootRef = useRef(document.getElementById('root'))

  useLayoutEffect(() => {
    const node = contentRef.current
    const reactRoot = reactRootRef.current

    if (!node || !reactRoot) {
      return
    }

    if (animState === 'expanding') {
      // Move to root and kick off expand animation
      reactRoot.appendChild(node)
      requestAnimationFrame(() => {
        setAnimState('expanded')
      })
    } else if (animState === 'idle') {
      // Move back to inline slot (after collapse finishes)
      if (inlineSlotRef.current && node.parentNode !== inlineSlotRef.current) {
        inlineSlotRef.current.appendChild(node)
      }
    }

    // No cleanup here — node must stay in #root during 'expanded' and
    // 'collapsing' states. Unmount cleanup is handled separately below.
  }, [animState])

  // Move node back to inline slot on component unmount
  useEffect(() => {
    return () => {
      const node = contentRef.current
      const reactRoot = reactRootRef.current

      if (
        node &&
        reactRoot &&
        node.parentNode === reactRoot &&
        inlineSlotRef.current
      ) {
        inlineSlotRef.current.appendChild(node)
      }
    }
  }, [])

  // Handle collapse transition end — signal that animation is done.
  // Only react to transitions on the content div itself (not bubbled from children).
  const handleTransitionEnd = useCallback(
    (event: React.TransitionEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget) {
        return
      }

      if (animState === 'collapsing') {
        setAnimState('idle')
      }
    },
    [animState]
  )

  const portalTarget = reactRootRef.current ?? document.body

  const showFilterBar =
    fullSizeDialogOpen &&
    (dashboardFilter.length > 0 || Boolean(periodFilter)) &&
    dashboard

  return (
    <>
      <ThemeProvider theme={widgetTheme}>
        <WidgetBase
          customization={customization}
          disableGutter={disableGutter}
          editMode={editMode}
          plain={plain}
          scaleFactor={scaleFactor}
          title={widget.title}
          widgetType={widget.type}
        >
          <div ref={inlineSlotRef} style={{ height: '100%' }}>
            <div
              ref={contentRef}
              style={
                useFullSizeLayout
                  ? {
                      position: 'fixed',
                      ...((animState === 'expanding' ||
                        animState === 'collapsing') &&
                      originRectRef.current
                        ? {
                            top: originRectRef.current.top,
                            left: originRectRef.current.left,
                            width: originRectRef.current.width,
                            height: originRectRef.current.height
                          }
                        : {
                            top: 16,
                            left: 16,
                            width: `calc(100vw - 32px - ${
                              settingsOpen ? SETTINGS_DRAWER_SX.width : '0px'
                            })`,
                            height: 'calc(100vh - 32px)',
                            ...(settingsOpen
                              ? {
                                  maxWidth: `calc(100vw - 32px - ${SETTINGS_DRAWER_SX.minWidth}px)`,
                                  minWidth: `calc(100vw - 32px - ${SETTINGS_DRAWER_SX.maxWidth}px)`
                                }
                              : null)
                          }),
                      zIndex: theme.zIndex.modal - 1,
                      background:
                        customization?.background_colors?.paper ||
                        widgetTheme.palette.background.paper,
                      borderRadius: 12,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                      padding: 16,
                      minWidth: 0,
                      overflow: 'auto',
                      transition:
                        'top 300ms ease, left 300ms ease, width 300ms ease, height 300ms ease'
                    }
                  : {
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      minWidth: 0,
                      position: 'relative',
                      overflow: 'hidden',
                      padding:
                        widget.type !== WidgetType.IMAGE ? 16 * scaleFactor : 0
                    }
              }
              onMouseDown={
                useFullSizeLayout
                  ? (event) => event.stopPropagation()
                  : undefined
              }
              onTransitionEnd={handleTransitionEnd}
            >
              <ThemeProvider theme={widgetTheme}>
                <WidgetMenu
                  availableSpace={availableSpace}
                  customSegmentBy={customSegmentBy}
                  dashboardFilterOptions={dashboardFilterOptions}
                  editMode={editMode}
                  embedded={embedded}
                  formattedData={formattedData}
                  fullSize={fullSizeDialogOpen}
                  isHomepalOrAdmin={isAdmin}
                  loading={false}
                  mobile={isMobile}
                  scaleFactor={activeScaleFactor}
                  showUnderlyingContent={handleShowUnderlyingContent}
                  setFullSizeDialogOpen={(open) =>
                    open ? handleOpenFullSize() : handleCloseFullSize()
                  }
                  setEdit={() => setSettingsOpen(true)}
                  variantType={variantType}
                  widget={widget}
                />
                {isDashboardWidgetSettingsLoaded(widget) ? (
                  <BreakdownBanner
                    customization={customization}
                    customSegmentBy={customSegmentBy}
                    fullSize={false}
                    onClear={() => setCustomSegmentBy(null)}
                    widgetSettings={widget.settings}
                  />
                ) : null}
                <Collapse in={!customSegmentBy}>
                  <WidgetHeader
                    editMode={editMode}
                    scaleFactor={activeScaleFactor}
                    updateWidget={updateWidget}
                    widget={widget}
                  />
                </Collapse>
                {showFilterBar ? (
                  <DashboardUserFilterBar
                    dashboard={dashboard}
                    sx={{ border: 'none', p: 0, mb: 1 }}
                  />
                ) : null}
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    minHeight: 0,
                    backgroundColor: useFullSizeLayout
                      ? customization?.background_colors?.paper
                        ? customization?.background_colors?.paper
                        : undefined
                      : undefined,
                    p:
                      widget.type === WidgetType.IMAGE
                        ? 0
                        : useFullSizeLayout
                          ? 2
                          : 0,
                    borderRadius: useFullSizeLayout ? 1 : 0
                  }}
                >
                  <WidgetBody
                    dashboardFilter={dashboardFilter}
                    editMode={editMode}
                    embedded={embedded}
                    fakeLoading={fakeLoading}
                    fetchWidgetData={fetchWidgetData}
                    formattedData={formattedData}
                    fullSize={fullSizeDialogOpen}
                    interactive={interactive && !isMobile && !editMode}
                    isAdmin={isAdmin}
                    periodFilter={periodFilter}
                    scaleFactor={activeScaleFactor}
                    customSegmentBy={customSegmentBy}
                    order={order}
                    setDashboardFilter={setDashboardFilter}
                    showUnderlyingContent={handleShowUnderlyingContent}
                    setEdit={setSettingsOpen}
                    setCustomSegmentBy={setCustomSegmentBy}
                    setOrder={setOrder}
                    setViewport={setViewport}
                    standaloneWidget={standaloneWidget}
                    updateWidget={updateWidget}
                    variantType={variantType}
                    viewport={viewport}
                    widget={widget}
                  />
                </Box>
                <WidgetFooter
                  customPeriodFilter={customPeriodFilter}
                  embedded={embedded}
                  interactive={interactive && !isMobile && !editMode}
                  periodFilter={periodFilter}
                  scaleFactor={activeScaleFactor}
                  widget={widget}
                />
                {fullSizeDialogOpen ? (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="text" onClick={handleCloseFullSize}>
                      {t('global', 'close')}
                    </Button>
                  </Box>
                ) : null}
              </ThemeProvider>
            </div>
          </div>
        </WidgetBase>
      </ThemeProvider>

      {/*-- backdrop (portaled to body to escape grid transform) --*/}
      {fullSizeDialogOpen
        ? ReactDOM.createPortal(
          <Backdrop
            open={true}
            onClick={handleCloseFullSize}
            sx={{ zIndex: theme.zIndex.modal - 2 }}
          />,
          portalTarget
        )
        : null}

      <ThemeProvider theme={getTheme('light')}>
        {/*-- widget drawer --*/}
        <WidgetSettings
          onClose={() => setSettingsOpen(false)}
          open={settingsOpen}
          setViewport={setViewport}
          updateWidget={handleUpdateWidgetSettings}
          widget={widget}
          viewport={viewport}
        />
      </ThemeProvider>
    </>
  )
}

export default Widget
